import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Navigation, CheckCircle, Receipt, RefreshCw, ArrowLeftRight, XCircle, MinusCircle } from "lucide-react";
import { fetchOrders } from "../lib/api";
import { psGetOrderRecap } from "../lib/psApi";
import { usePsStore } from "../store/psStore";
import { chf, fmtDate } from "../lib/format";
import { metaFor } from "../components/ServiceMeta";

const STOP_LABEL = { MIGROS: "Migros", COOP: "Coop", ALDI: "Aldi", PHARMACY: "Pharmacie", KIOSK: "Kiosque" };

function PsOrderCard({ entry, nav }) {
  const [state, setState] = useState({ loading: true, recap: null, notFound: false });
  const removeFromHistory = usePsStore((s) => s.removeFromHistory);

  useEffect(() => {
    let live = true;
    psGetOrderRecap(entry.track_token)
      .then((recap) => live && setState({ loading: false, recap, notFound: false }))
      .catch((e) => live && setState({ loading: false, recap: null, notFound: e?.response?.status === 404 }));
    return () => { live = false; };
  }, [entry.track_token]);

  const { loading, recap, notFound } = state;
  const inProgress = recap?.active && !recap?.finished;
  const finished = recap?.finished;
  const subs = recap?.substitutions;
  const hasSubs = subs && subs.replaced.length + subs.refused.length + subs.absent.length > 0;

  return (
    <div className="bg-white rounded-2xl p-5 mb-4" style={{ border: "1px solid #f0f0f0" }} data-testid={`ps-order-${entry.order_number}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-base font-extrabold" style={{ color: "#0b1220" }}>Commande #{recap?.order_number || entry.order_number}</p>
          <p className="text-xs mt-1" style={{ color: "#94a3b8" }}>{fmtDate(recap?.created_at || entry.placed_at)}</p>
        </div>
        {loading ? <RefreshCw size={18} className="animate-spin" style={{ color: "#16a34a" }} /> : (
          <span className="text-xs font-bold px-3 py-1 rounded-full"
            style={{ background: finished ? "#ecfdf5" : inProgress ? "#fffbeb" : "#f1f5f9", color: finished ? "#16a34a" : inProgress ? "#b45309" : "#475569" }}>
            {recap?.status_label || (notFound ? "Introuvable" : "—")}
          </span>
        )}
      </div>

      {notFound && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs" style={{ color: "#94a3b8" }}>Cette commande n'est plus disponible.</span>
          <button onClick={() => removeFromHistory(entry.order_id)} className="text-xs font-bold" style={{ color: "#b91c1c" }}>Retirer</button>
        </div>
      )}

      {recap && (
        <>
          {recap.services?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {recap.services.map((sv, i) => {
                const m = metaFor(sv.stop_type, sv.store_name);
                const Icon = m.Icon;
                return (
                  <span key={i} className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: "#f0fdfa", color: "#0f766e", border: "1px solid #99f6e4" }}>
                    <Icon size={13} color={m.accent} /> {STOP_LABEL[sv.stop_type] || sv.store_name || sv.stop_type}
                  </span>
                );
              })}
            </div>
          )}

          <div className="flex items-center justify-between mt-4 pt-3" style={{ borderTop: "1px solid #f1f5f9" }}>
            <span className="text-sm font-semibold" style={{ color: "#475569" }}>Montant final</span>
            {recap.finalOrderAmountChf != null
              ? <span className="text-lg font-extrabold" style={{ color: "#0b1220" }} data-testid={`ps-order-total-${entry.order_number}`}>{chf(recap.finalOrderAmountChf)}</span>
              : <span className="text-sm font-bold" style={{ color: "#b45309" }}>{finished ? "—" : "En cours"}</span>}
          </div>

          {hasSubs && (
            <div className="mt-3 rounded-xl p-3" style={{ background: "#f8fafc" }}>
              <p className="text-xs font-extrabold tracking-wide mb-2" style={{ color: "#64748b" }}>REMPLACEMENTS</p>
              {subs.replaced.map((x, i) => <div key={`r${i}`} className="flex items-center gap-2 py-0.5 text-xs" style={{ color: "#475569" }}><ArrowLeftRight size={14} color="#16a34a" /><span><b>{x.original_name}</b> → {x.replacement_name}</span></div>)}
              {subs.refused.map((x, i) => <div key={`x${i}`} className="flex items-center gap-2 py-0.5 text-xs" style={{ color: "#475569" }}><XCircle size={14} color="#b91c1c" /><span><b>{x.original_name}</b> — remplacement refusé</span></div>)}
              {subs.absent.map((x, i) => <div key={`a${i}`} className="flex items-center gap-2 py-0.5 text-xs" style={{ color: "#475569" }}><MinusCircle size={14} color="#64748b" /><span><b>{x.original_name}</b> — non disponible</span></div>)}
            </div>
          )}

          {recap.receipts?.length > 0 && (
            <div className="mt-3 rounded-xl p-3" style={{ background: "#f8fafc" }}>
              <p className="text-xs font-extrabold tracking-wide mb-2" style={{ color: "#64748b" }}>TICKETS</p>
              {recap.receipts.map((r) => (
                <div key={r.receipt_id} className="flex items-center gap-2 py-1 text-xs" style={{ color: "#475569" }}>
                  <Receipt size={14} /> <span className="flex-1">{r.retailer || "Ticket"}{r.receipt_date ? ` · ${r.receipt_date}` : ""}</span>
                  <b>{r.total_amount_chf != null ? chf(r.total_amount_chf) : "—"}</b>
                </div>
              ))}
            </div>
          )}

          {inProgress ? (
            <button onClick={() => nav(`/personal-shopper/suivi/${entry.track_token}?n=${recap.order_number}`)} data-testid={`ps-order-track-${entry.order_number}`}
              className="w-full rounded-xl font-bold text-white py-3 mt-4 flex items-center justify-center gap-2" style={{ background: "#0b1220" }}>
              <Navigation size={18} /> Suivre ma commande
            </button>
          ) : finished ? (
            <div className="flex items-center gap-2 mt-3 text-sm font-semibold" style={{ color: "#16a34a" }}><CheckCircle size={16} /> Commande terminée</div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function Orders() {
  const nav = useNavigate();
  const history = usePsStore((s) => s.history);
  const [tab, setTab] = useState("ps");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    fetchOrders().then((o) => setOrders(Array.isArray(o) ? o : [])).catch(() => setOrders([])).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-extrabold mb-5" style={{ color: "#0b1220" }}>Mes commandes</h1>

      <div className="flex gap-2 mb-6">
        {[["ps", "Courses & Services"], ["delivery", "Boutique / Livraison"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`orders-tab-${k}`}
            className="px-4 py-2 rounded-full text-sm font-bold"
            style={{ background: tab === k ? "#0b1220" : "#fff", color: tab === k ? "#fff" : "#0b1220", border: "1px solid #e5e7eb" }}>
            {l}
          </button>
        ))}
      </div>

      {tab === "ps" ? (
        history.length === 0 ? (
          <div className="text-center py-16" data-testid="ps-orders-empty">
            <Package size={48} className="mx-auto mb-3" style={{ color: "#cbd5e1" }} />
            <p className="font-bold" style={{ color: "#475569" }}>Aucune commande pour le moment.</p>
            <p className="text-sm mt-1" style={{ color: "#94a3b8" }}>Vos commandes Courses &amp; Services apparaîtront ici.</p>
          </div>
        ) : history.map((h) => <PsOrderCard key={h.order_id} entry={h} nav={nav} />)
      ) : loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 rounded-2xl animate-pulse" style={{ background: "#e9edf2" }} />)}</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16">
          <Package size={48} className="mx-auto mb-3" style={{ color: "#cbd5e1" }} />
          <p className="font-bold" style={{ color: "#475569" }}>Aucune commande de livraison.</p>
        </div>
      ) : orders.map((o) => (
        <div key={o.id} className="bg-white rounded-2xl p-5 mb-4" style={{ border: "1px solid #f0f0f0" }} data-testid={`order-${o.id}`}>
          <div className="flex items-start justify-between">
            <div>
              <p className="font-extrabold" style={{ color: "#0b1220" }}>Commande</p>
              <p className="text-xs mt-1" style={{ color: "#94a3b8" }}>{fmtDate(o.created_at)}</p>
            </div>
            <span className="text-xs font-bold px-3 py-1 rounded-full" style={{ background: "#f1f5f9", color: "#475569" }}>{o.status}</span>
          </div>
          <p className="text-sm mt-3" style={{ color: "#64748b" }}>{o.items?.length || 0} article(s) · {o.customer_address}</p>
          <div className="flex justify-between items-center mt-3 pt-3" style={{ borderTop: "1px solid #f1f5f9" }}>
            <span className="text-sm font-semibold" style={{ color: "#475569" }}>Total</span>
            <span className="text-lg font-extrabold" style={{ color: "#0b1220" }}>{chf(o.total_amount)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}
